<template>
    <div>
        <h4 class="text-uppercase my-2" v-if="!isProgram">
            Internal notes about this {{ userType }}'s account
        </h4>
        <h4 class="text-uppercase my-2" v-else="">Program notes</h4>
        <div class="table-header font-weight-bold text-white w-100 p-2">Notes</div>
        <div class="bg-white w-100 border-light px-3 mb-4">
            <div class="row mt-2">
                <div class="col-12 col-md-2 my-2">
                    <v-date-picker
                        dusk="date"
                        placeholder="MM/DD/YYYY"
                        id="date"
                        class="picker-container"
                        popover-align="left"
                        v-model="newNote.date"
                    ></v-date-picker>
                </div>
                <div class="col-12 col-md-2 my-2" v-if="!isProgram">
                    <select
                        v-model="newNote.label"
                        name=""
                        id="label"
                        class="form-control form-control-sm"
                    >
                        <option selected disabled hidden :value="null">Choose label...</option>
                        <option v-for="l in labels" :value="l">{{ l }}</option>
                    </select>
                </div>
                <div class="col-12 col-md-4 my-2" :class="isProgram ? 'col-md-6' : 'col-md-4'">
                    <textarea
                        id="notesData"
                        class="form-control h-100"
                        v-model="newNote.note"
                        placeholder="Notes"
                    ></textarea>
                </div>
                <div class="col-12 col-md-3 my-2">
                    <label for="file"
                        >Attach a file
                        <i
                            class="fa fa-info-circle text-info"
                            v-tooltip="`File size limited to 50MB`"
                        ></i
                    ></label>
                    <input
                        id="file"
                        name="file"
                        type="file"
                        class="form-control-file"
                        ref="file"
                        @change="handleFile"
                    />
                </div>
                <div class="col-12 col-md-1 my-2">
                    <div class="save-button-container mb-2">
                        <button
                            :disabled="loading"
                            @click.prevent="handleSave"
                            class="btn btn-sm btn-primary font-weight-bold text-white text-uppercase d-inline-block"
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="error" class="alert alert-danger"><strong>Error!</strong> {{ error }}</div>

            <div v-if="success" class="alert alert-success">
                <strong>Success!</strong> Your note was saved
            </div>

            <template v-if="!isProgram">
                <transition name="squish-vert-fade">
                    <div v-if="!showSearch">
                        <div class="d-flex w-100 justify-content-end mb-3">
                            <span
                                @click="showSearch = true"
                                class="text-primary font-italic cursor-pointer small"
                            >
                                <span>Show filter options</span>
                                <i class="ml-2 fa fa-angle-down"></i>
                            </span>
                        </div>
                    </div>
                    <div v-else="">
                        <div class="d-flex w-100 justify-content-end">
                            <span
                                @click="showSearch = false"
                                class="text-primary font-italic cursor-pointer small"
                            >
                                <span>Hide filter options</span>
                                <i class="ml-2 fa fa-angle-up"></i>
                            </span>
                        </div>
                        <div class="d-flex text-right justify-content-end my-3">
                            <input
                                type="text"
                                v-model="search"
                                id="search"
                                class="form-control-sm form-control form-inline"
                                placeholder="Search by author, label or note..."
                            />
                            <div class="dropdown">
                                <button
                                    class="btn btn-outline-primary btn-sm dropdown-toggle ml-3 "
                                    type="button"
                                    id="labels"
                                    data-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                >
                                    Filter by labels
                                </button>
                                <div class="dropdown-menu px-3" aria-labelledby="labels">
                                    <div class="form-check" v-for="lab in labels">
                                        <input
                                            type="checkbox"
                                            class="form-check-input"
                                            :id="lab"
                                            v-model="selectedLabels"
                                            :value="lab"
                                        />
                                        <label class="form-check-label text-capitalize" :for="lab">
                                            {{ lab }}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </transition>
            </template>

            <div v-if="loading" class="loading-container">
                <i class="fa fa-spin fa-circle-o-notch mr-2"></i>
            </div>

            <table class="table table-striped">
                <colgroup>
                    <col span="1" style="width: 14.0%;" />
                    <col span="1" style="width: 8.0%;" />
                    <col span="1" style="width: 12.0%;" v-if="!isProgram" />
                    <col span="1" />
                    <col span="1" style="width: 8.0%;" />
                    <col span="1" style="width: 8.0%;" />
                </colgroup>
                <thead>
                    <tr>
                        <th scope="col">Date</th>
                        <th scope="col">Author</th>
                        <th scope="col" v-if="!isProgram">Label</th>
                        <th scope="col">Note</th>
                        <th scope="col">File</th>
                        <th scope="col"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="n in filteredNotes" :key="n.id">
                        <td>{{ n.date ? moment(n.date).format("MM/DD/YYYY") : "Not entered" }}</td>
                        <td>
                            <profile-picture
                                v-if="n.author_profile_photo_url"
                                :name="n.author_name"
                                :size="'sm'"
                                :url="n.author_profile_photo_url"
                            ></profile-picture>
                        </td>
                        <td v-if="!isProgram" class="text-capitalize">{{ n.label }}</td>
                        <td>{{ n.body }}</td>
                        <td>
                            <a
                                data-toggle="tooltip"
                                data-placement="top"
                                v-bind:href="n.file_url"
                                v-bind:title="n.filename"
                                ><i :class="n.file_url ? 'fa fa-file' : ''"></i
                            ></a>
                        </td>
                        <td>
                            <template>
                                <div class="dropdown text-right">
                                    <button
                                        class="btn btn-sm btn-link text-black"
                                        type="button"
                                        data-toggle="dropdown"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                    >
                                        <i class="fa fa-ellipsis-v"></i>
                                    </button>
                                    <div
                                        class="dropdown-menu dropdown-menu-right"
                                        aria-labelledby="delete-recurring"
                                    >
                                        <a
                                            class="cursor-pointer dropdown-item small"
                                            @click="showEditNote(n)"
                                            >Edit Note</a
                                        >
                                        <a
                                            class="cursor-pointer dropdown-item small text-danger"
                                            @click="confirmDeleteNote(n.id)"
                                            >Delete Note</a
                                        >
                                    </div>
                                </div>
                            </template>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <modal-component
            :id="'modal-note'"
            :visible="modal.visible"
            :title="modal.title"
            :size="modal.size"
            @close="resetModal()"
        >
            <div v-show="selectedAction === 'delete'">
                <p class="">
                    Are you sure you want to delete this note? All of the data associated with this
                    note will be permanently removed.
                </p>
                <a
                    class="btn btn-sm btn-primary font-weight-bold text-white text-uppercase btn-block m-1"
                    @click="deleteNote()"
                    ><small
                        v-html="
                            loading
                                ? `<i class='fa fa-spin fa-circle-o-notch mr-2'></i> Deleting...`
                                : `Yes, I want to permanently delete this note`
                        "
                    ></small
                ></a>
            </div>
            <div v-show="selectedAction === 'edit'">
                <form>
                    <div class="form-group row mb-2">
                        <label
                            for="note-date"
                            class="col-4 col-form-label font-weight-bold text-nowrap text-capitalize"
                            >Date:</label
                        >
                        <div class="col-8">
                            <v-date-picker
                                dusk="date"
                                placeholder="MM/DD/YYYY"
                                id="editdate"
                                class="picker-container"
                                popover-align="left"
                                v-model="editNote.date"
                            ></v-date-picker>
                        </div>
                    </div>

                    <div class="form-group row mb-2" v-if="!isProgram">
                        <label
                            for="note-label"
                            class="col-4 col-form-label font-weight-bold text-nowrap text-capitalize"
                            >Label:</label
                        >
                        <div class="col-8">
                            <select
                                v-model="editNote.label"
                                name=""
                                id="note-label"
                                class="form-control form-control-sm"
                            >
                                <option selected disabled hidden :value="null"
                                    >Choose label...</option
                                >
                                <option v-for="l in labels" :value="l">{{ l }}</option>
                            </select>
                        </div>
                    </div>

                    <div class="form-group row mb-2">
                        <label
                            for="note-data"
                            class="col-4 col-form-label font-weight-bold text-nowrap text-capitalize"
                            >Note:</label
                        >
                        <div class="col-8">
                            <textarea
                                id="note-data"
                                class="form-control h-100"
                                v-model="editNote.body"
                                placeholder="Notes"
                            ></textarea>
                        </div>
                    </div>

                    <div class="d-flex w-100 justify-content-end">
                        <button
                            :disabled="loading"
                            @click.prevent="patchNote()"
                            class="btn btn-sm btn-primary font-weight-bold text-white text-uppercase d-inline-block"
                        >
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </modal-component>
    </div>
</template>

<script>
import ModalComponent from "../ui/ModalComponent";
import ProfilePicture from "../ui/ProfilePicture";
import { fixDatePickers } from "../../plugins/fixDatePickers";

export default {
    name: "InternalNotes",
    props: ["userType", "apiEndpoints", "isProgram"],
    mixins: [fixDatePickers],
    components: {
        ModalComponent,
        ProfilePicture,
    },
    data() {
        return {
            showSearch: false,
            search: "",
            labels: ["billing", "admin", "personal", "contract", "report", "other"],
            selectedLabels: ["billing", "admin", "personal", "contract", "report", "other"],
            newNote: this.getInitialNewNoteData(),
            currentNote: null,
            editNote: {},
            notes: [],
            error: null,
            success: false,
            loading: false,
            selectedAction: null,
            modal: {
                visible: false,
                size: null,
                title: null,
            },
        };
    },
    mounted() {
        if (
            this.isProgram ||
            (this.apiEndpoints &&
                !this.notes.length &&
                !this.apiEndpoints.get.includes("undefined"))
        ) {
            this.getNotesData();
        }
    },
    computed: {
        sortedNotes() {
            // sort by ascending age
            return this.notes.sort((a, b) => {
                return new Date(b.created_at) - new Date(a.created_at);
            });
        },
        filteredNotes() {
            return this.sortedNotes.filter(note => {
                let match = false;
                Object.values(note).forEach(value => {
                    if (
                        value &&
                        value
                            .toString()
                            .toLowerCase()
                            .includes(this.search.toLowerCase().trim())
                    ) {
                        match = true;
                    }
                });
                return (match && this.selectedLabels.includes(note.label)) || this.isProgram;
            });
        },
    },
    watch: {
        apiEndpoints() {
            if (!this.apiEndpoints.get.includes("undefined")) {
                this.getNotesData();
            }
        },
    },
    methods: {
        confirmDeleteNote(id) {
            this.selectedAction = "delete";
            this.currentNote = id;
            this.modal.size = "sm";
            this.modal.visible = true;
        },
        showEditNote(note) {
            this.selectedAction = "edit";
            this.editNote = { ...note };
            this.editNote.date = this.moment(this.editNote.date).toDate();
            this.modal.title = "Edit Note";
            this.modal.size = null;
            this.modal.visible = true;
        },
        resetModal() {
            this.modal.visible = false;
            this.modal.title = null;
            this.modal.size = null;
            this.currentNote = null;
            this.editNote = {};
        },
        getNotesData() {
            this.loading = true;
            axios
                .get(this.apiEndpoints.get)
                .then(r => {
                    console.log(this.isProgram);
                    if (!this.isProgram) {
                        // Gets all notes and filters out program notes
                        this.notes = r.data.data.filter(n => !["program"].includes(n.label));
                    } else {
                        this.notes = r.data.data.filter(n => ["program"].includes(n.label));
                    }
                    this.error = null;
                    this.loading = false;
                    $('[data-toggle="tooltip"]').tooltip();
                })
                .catch(e => {
                    console.error(e);
                    this.error = "Could not retrieve internal notes. Server error.";
                    this.loading = false;
                    $('[data-toggle="tooltip"]').tooltip();
                });
        },
        patchNote() {
            let payload = { ...this.editNote };
            payload.date = this.moment(payload.date).format("YYYY-MM-DD");
            payload.author_name = this.$root.user.name;
            axios
                .patch(`${this.apiEndpoints.post}/${payload.id}`, payload)
                .then(r => {
                    this.getNotesData();
                    this.resetModal();
                    this.loading = false;
                })
                .catch(e => {
                    if (_.get(e, "response.status", null) === 403) {
                        this.$swal({
                            title: "Error",
                            text: `You don't have the required permission to edit this entry.`,
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    } else {
                        this.$swal({
                            title: "Error",
                            text: "Could not save. Please check your values or try again later.",
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    }
                    this.resetModal();
                    this.loading = false;
                });
        },
        handleSave(e) {
            const payload = new FormData();
            payload.append("date", this.moment(this.newNote.date).format("YYYY-MM-DD"));
            payload.append("body", this.newNote.note);
            payload.append("label", this.newNote.label);
            if (this.newNote.file) {
                payload.append("file", this.newNote.file);
            }

            this.loading = true;
            axios
                .post(this.apiEndpoints.post, payload)
                .then(r => {
                    // add note locally
                    this.notes.push(r.data.data);
                    // reset form state
                    this.error = null;
                    this.newNote = this.getInitialNewNoteData();
                    this.$refs.file.value = "";
                    // show success banner
                    this.success = true;
                    const self = this;
                    setTimeout(() => {
                        self.success = false;
                    }, 3000);
                    this.loading = false;
                })
                .catch(e => {
                    if (_.get(e, "response.status", null) === 403) {
                        this.$swal({
                            title: "Error",
                            text: `You don't have the required permission to save this entry.`,
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    } else {
                        this.$swal({
                            title: "Error",
                            text: "Could not save. Please check your values or try again later.",
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    }
                    this.loading = false;
                });
        },
        handleFile() {
            const files = this.$refs.file.files;
            this.newNote.file = files[0];
        },
        getInitialNewNoteData() {
            return {
                date: new Date(),
                label: this.isProgram ? "program" : null,
                note: null,
                file: null,
            };
        },
        openActionsMenu(noteId) {
            this.currentNote = noteId;
            this.selectedAction = null;
            this.modal.visible = true;
            this.modal.title = "Note";
        },
        deleteNote() {
            this.loading = true;
            axios
                .delete(this.apiEndpoints.post + "/" + this.currentNote)
                .then(response => {
                    this.loading = false;
                    this.resetModal();
                    this.getNotesData();
                })
                .catch(e => {
                    if (_.get(e, "response.status", null) === 403) {
                        this.$swal({
                            title: "Error",
                            text: `You don't have the required permission to delete this entry.`,
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    } else {
                        this.$swal({
                            title: "Error",
                            text:
                                "Could not delete. If this error persists, please contact our support.",
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    }
                    console.error(e);
                });
        },
    },
};
</script>

<style scoped lang="scss">
@import "../../../sass/_variables.scss";
.save-button-container {
    text-align: center;
}
@media #{$breakpoint-small} {
    .save-button-container {
        text-align: right;
    }
}

.overlay {
    opacity: 0.2;
}

.actions {
    z-index: 3;
    width: 280px;
    top: 1rem;
    right: 0;
    left: 0;
    margin: 0 auto;
    border: solid 1px #8f8f8f;
    opacity: 1;
    > a {
        opacity: 1;
    }
}
.cell {
    font-size: 0.8rem;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    //align-items: center;
}

.table th,
.table td {
    padding: 0.15rem;
}
@media #{$breakpoint-small} {
    .table th,
    .table td {
        padding: 0.45rem;
    }
}
@media #{$breakpoint-medium} {
    .table th,
    .table td {
        padding: 0.75rem;
    }
}

.loading-container {
    text-align: center;
}
</style>
